import { Controller } from '@hotwired/stimulus'
import { Fancybox } from '@fancyapps/ui'
import { Howl, Howler } from 'howler'
import { load } from 'js-yaml'

export default class extends Controller {
  static targets = [
    'volume',
    'volumeSlider',
    'muteButton',
    'loader',
    'location',
    'locationName',
    'crimeName',
    'image',
    'trackName',
    'strongbad',
    'strongbadStanding',
    'strongbadFace',
    'strongbadWrapper',
    'bubbleWrapper',
    'bubble',
    'openMouth',
    'minutes',
    'seconds',
    'timerButton',
    'guiltCheck',
    'songName',
    'musicPlayer',
    'tutorial'
  ]
  static values = {
    volume: { type: Number, default: 0.25 },
    musicVolume: { type: Number, default: 0.25 },
    muted: { type: Boolean, default: false },
    musicPlaying: { type: Boolean, default: false },
    bgmPlaying: { type: Number, default: 99 },
    musicShuffle: { type: Boolean, default: false },
    musicLoop: { type: Boolean, default: false },
    location: { type: Number, default: 99 },
    crimesLoaded: { type: Boolean, default: false },
    imagesLoaded: { type: Boolean, default: false },
    musicLoaded: { type: Boolean, default: false },
    tauntsLoaded: { type: Boolean, default: false },
    ready: { type: Boolean, default: false },
    begun: { type: Boolean, default: false },
    strongbad: { type: Boolean, default: false },
    strongbadAnimation: { type: String, default: 'strongbad-standing1' },
    strongbadTalking: { type: Boolean, default: false },
    menuVisible: { type: Boolean, default: false },
    time: { type: Number, default: 0 },
    timerRunning: { type: Boolean, default: false },
    guiltCheck: { type: Boolean, default: false },
    tauntHistory: { type: Array, default: [] },
    crimeHistory: { type: Array, default: [] },
    musicWasPlaying: { type: Boolean, default: false }
  }

  connect() {
    this.timerTimeout = null
    this.bgm = []
    this.bgmData = []
    this.bgmHistory = []
    this.taunts = []

    this.initiateSFX()
    this.initiateBGM()
    this.initiateCrimes()
    this.initiateTaunts()

    this.volumeSliderTarget.value = this.volumeValue * 100

    // Make sure all important images are loaded
    this.imagesLoadedCount = 0
    this.imageTargets.forEach(img => {
      if (img.complete) {
        this.onImageLoad()
      } else {
        img.addEventListener("load", () => this.onImageLoad())
      }
    })
  }

  onImageLoad() {
    this.imagesLoadedCount++
    if (this.imagesLoadedCount === this.imageTargets.length) {
      this.imagesLoadedValue = true
    }
  }

  crimesLoadedValueChanged() {
    this.checkReadiness()
  }

  imagesLoadedValueChanged() {
    this.checkReadiness()
  }

  musicLoadedValueChanged() {
    this.checkReadiness()
  }

  tauntsLoadedValueChanged() {
    this.checkReadiness()
  }

  checkReadiness() {
    if (this.crimesLoadedValue && this.imagesLoadedValue && this.musicLoadedValue) {
      this.readyValue = true
    }
  }

  readyValueChanged() {
    this.loaderTarget.classList.add('opacity-0')
  }

  hideTutorial() {
    this.tutorialTarget.classList.add('hidden')
    this.menuVisibleValue = false
    if (!this.begunValue) {
      this.begin()      
    }
  }

  showTutorial() {
    this.menuVisibleValue = true
    this.tutorialTarget.classList.remove('hidden')
  }

  begin() {
    console.log('Begin!')
    this.playBGM(0)
    this.shuffleCrime()
    this.begunValue = true
  }

  initiateSFX() {
    this.sfx = {}
    this.sfx.buzzer = new Howl({ src: ['/assets/sfx/buzzer_deleted.mp3'] })
    this.sfx.typeout = new Howl({ src: ['/assets/sfx/text_typeout.mp3'] })
    this.sfx.timerCrescendo = new Howl({ src: ['/assets/sfx/timer_crescendo2.mp3'] })
    this.sfx.guiltcheck = new Howl({ src: ['/assets/sbquotes/sb_guiltcheck.mp3'] })
  }

  stopAllSFX() {
    for (const sfx of this.sfx) {
      sfx.stop()
    }
  }

  async initiateBGM() {
    const res = await fetch('/assets/data/music.yml')
    const text = await res.text()
    const bgm = load(text)

    for (const song of bgm) {
      this.bgmData.push(song)
    }
    for (const bgm of this.bgmData) {
      const howl = new Howl({ src: [bgm.file], loop: true, volume: this.musicVolumeValue })
      howl.on('end', () => this.endOfSong())
      this.bgm.push(howl)
    }

    this.musicLoadedValue = true
  }

  async initiateCrimes() {
    const res = await fetch('/assets/data/crimes.yml')
    const text = await res.text()
    const crimes = load(text)

    this.crimes = {}
    this.crimes[1] = crimes.parts1
    this.crimes[2] = crimes.parts2
    this.crimes[3] = crimes.parts3

    this.crimesLoadedValue = true
  }

  async initiateTaunts() {
    const res = await fetch('/assets/data/taunts.yml')
    const text = await res.text()
    const taunts = load(text)

    for (let partIndex = 1; partIndex <= 3; partIndex++) {
      this.taunts[partIndex] = []

      for (const taunt of taunts[`parts${partIndex}`]) {
        const part = {}
        part.text = taunt.text
        const filename = `/assets/taunt_generator/${taunt.audio}`
        part.howl = new Howl({ src: [ filename ], preload: true })

        this.taunts[partIndex].push(part)
      }
    }

    this.tauntsLoadedValue = true
  }

  adjustVolume() {
    this.volumeValue = (this.volumeSliderTarget.value / 100.0)

    if (this.volumeValue > 0) {
      this.mutedValue = false
      Howler.volume(this.volumeValue)
    } else {
      this.mutedValue = true
    }
  }

  toggleMute() {
    this.mutedValue = !this.mutedValue
  }

  mutedValueChanged() {
    if (this.hasMuteButtonTarget) {
      if (this.mutedValue) {
        this.muteButtonTarget.classList.add('muted')
        this.muteButtonTarget.classList.remove('unmuted')
      } else {
        this.muteButtonTarget.classList.remove('muted')
        this.muteButtonTarget.classList.add('unmuted')
      }
    }
    if (this.mutedValue) {
      Howler.mute(true)
      Howler.volume(0)
    } else {
      Howler.mute(false)
      Howler.volume(this.volumeValue)
    }
  }

  randomBGM() {
    let diceRoll = this.bgmPlayingValue
    while (diceRoll == this.bgmPlayingValue) {
      diceRoll = Math.floor(Math.random() * this.bgm.length)
    }
    this.playBGM(diceRoll)
  }

  playBGM(track) {
    if (this.bgmPlayingValue != 99 && this.bgmPlayingValue != track) {
      this.bgm[this.bgmPlayingValue].stop()
    }
    this.bgmPlayingValue = track
    this.bgmHistory.push(track)
    this.songNameTarget.innerHTML = this.bgmData[this.bgmPlayingValue].title
    this.resumeBGM()
  }

  pauseBGM() {
    if (this.bgmPlayingValue != 99 && this.bgm[this.bgmPlayingValue].playing()) {
      this.bgm[this.bgmPlayingValue].pause()
    }
    this.musicPlayingValue = false
  }

  resumeBGM() {
    if (this.bgmPlayingValue != 99 && !this.bgm[this.bgmPlayingValue].playing()) {
      this.bgm[this.bgmPlayingValue].play()
    }
    this.musicPlayingValue = true
  }

  toggleMusic() {
    if (this.musicPlayingValue) {
      this.pauseBGM()
    } else {
      this.resumeBGM()
    }
  }

  nextSong() {
    if (this.musicShuffleValue) {
      this.randomBGM()
    } else {
      let nextTrack = this.bgmPlayingValue + 1
      if (nextTrack >= this.bgm.length) {
        nextTrack = 0
      }
      this.playBGM(nextTrack)
    }
  }

  prevSong() {
    if (this.bgmHistory.length > 1) {
      this.bgm[this.bgmPlayingValue].stop()
      this.bgmHistory.pop()
      const lastTrack = this.bgmHistory.pop()
      this.bgmPlayingValue = lastTrack
      this.songNameTarget.innerHTML = this.bgmData[this.bgmPlayingValue].title
      this.resumeBGM()
    } else {
      this.bgm[this.bgmPlayingValue].seek(0)
    }
  }

  musicPlayingValueChanged() {
    if (this.musicPlayingValue) {
      this.musicPlayerTarget.classList.add('started')
    } else {
      this.musicPlayerTarget.classList.remove('started')
    }
  }

  shuffleCrime() {
    let crime = []
    for (let x = 1; x <= 3; x++) {
      let newCrime = ''
      while (newCrime == '' || this.crimeHistoryValue.includes(newCrime)) {
        const diceRoll = Math.floor(Math.random() * this.crimes[x].length)
        newCrime = this.crimes[x][diceRoll]
      }
      crime.push(newCrime)

      this.crimeHistoryValue.push(newCrime)
      if (this.crimeHistoryValue.length >= 9) {
        this.crimeHistoryValue.shift()
      }
    }
    const crimeString = crime.join(' ')
    const crimeHTML = [...crimeString].map((char, i) => `<span class='opacity-0'>${char}</span>`).join('')

    this.crimeNameTarget.innerHTML = crimeHTML
    this.crimeNameTarget.classList.remove('crime-animated')
    this.sfx.typeout.stop()
    this.sfx.typeout.play()
    this.animateCrime()
  }

  animateCrime() {
    const crimeSpans = this.crimeNameTarget.querySelectorAll('.opacity-0')
    if (crimeSpans.length > 0) {
      let lettersToReveal = 4
      for (const span of crimeSpans) {
        if (lettersToReveal > 0) {
          span.classList.remove('opacity-0')
          lettersToReveal -= 1
        }
      }
      setTimeout(this.animateCrime.bind(this), 83.33)
    } else {
      this.sfx.typeout.stop()
    }
  }

  shuffleLocation() {
    // Roll until we get a new location
    let diceRoll = Math.floor(Math.random() * this.locationTargets.length)
    while (diceRoll == this.locationValue) {
      diceRoll = Math.floor(Math.random() * this.locationTargets.length)
    }
    this.locationValue = diceRoll

    const newLocation = this.locationTargets[this.locationValue]
    const newLocationName = newLocation.getAttribute('alt')

    for (const location of this.locationTargets) {
      if (location.getAttribute('alt') != newLocationName) {
        location.classList.add('opacity-0')
      }
    }
    newLocation.classList.remove('opacity-0')

    this.locationNameTarget.innerHTML = newLocationName
  }

  randomTaunt() {
    if (this.strongbadValue == false) {
      this.strongbadValue = true

      setTimeout(this.showBubble.bind(this), 250)

      let taunt = []
      for (let x = 1; x <= 3; x++) {
        let newTauntText = ''
        let chosenPart = null
        while (newTauntText == '' || this.tauntHistoryValue.includes(newTauntText)) {
          const diceRoll = Math.floor(Math.random() * this.taunts[x].length)
          chosenPart = this.taunts[x][diceRoll]
          newTauntText = chosenPart.text
        }

        this.tauntHistoryValue.push(newTauntText)
        if (this.tauntHistoryValue.length >= 9) {
          this.tauntHistoryValue.shift()
        }

        taunt.push(chosenPart)
      }

      this.playTaunt(taunt).then(() => {
        this.strongbadTalkingValue = false
        this.strongbadValue = false
        this.bubbleWrapperTarget.classList.add('hidden')
      })
    }
  }

  showBubble() {
    this.bubbleWrapperTarget.classList.remove('hidden')
  }

  async playTaunt(parts) {
    this.bubbleTarget.innerHTML = ''

    this.strongbadTalkingValue = true
    this.animateMouth()

    const bubbleFragment = document.createDocumentFragment()
    let partCount = 1
    for (const part of parts) {
      const span = document.createElement('span')
      span.classList.add('opacity-0')
      if (partCount == 2) {
        span.classList.add('ml-2')
      }
      span.setAttribute('id', `bubble_text_${partCount}`)
      span.innerHTML = part.text
      bubbleFragment.appendChild(span)
      partCount += 1
    }
    this.bubbleTarget.appendChild(bubbleFragment)

    let partsRevealed = 1
    for (const part of parts) {
      document.getElementById(`bubble_text_${partsRevealed}`).classList.remove('opacity-0')
      partsRevealed += 1
      await this.playAndWait(part.howl)
    }
  }

  playAndWait(howl) {
    return new Promise(resolve => {
      howl.once('end', resolve)
      howl.play()
    })
  }

  strongbadValueChanged() {
    if (this.strongbadValue == true) {
      if (this.guiltCheckValue == true) {
        this.strongbadAnimationValue = 'strongbad-guilt'
        this.hideAllStrongbads()
        this.showRandomStander()
      } else {
        const sbAnimation = Math.floor(Math.random() * 4) + 1
        this.hideAllStrongbads()
        if (sbAnimation == 4) {
          this.strongbadAnimationValue = 'strongbad-face1'
          this.showRandomFace()
        } else {
          this.strongbadAnimationValue = `strongbad-standing${sbAnimation}`
          this.showRandomStander()
        }
      }
      this.strongbadWrapperTarget.classList.remove('animate-strongbad-standing1-arrive', 'animate-strongbad-standing2-arrive', 'animate-strongbad-standing3-arrive', 'animate-strongbad-face1-arrive', 'animate-strongbad-guilt-arrive', 'animate-strongbad-standing1-leave', 'animate-strongbad-standing2-leave', 'animate-strongbad-standing3-leave', 'animate-strongbad-face1-leave', 'animate-strongbad-guilt-leave', 'opacity-1')
      this.strongbadWrapperTarget.classList.add(`animate-${this.strongbadAnimationValue}-arrive`)
    } else {
      this.strongbadWrapperTarget.classList.remove(`animate-${this.strongbadAnimationValue}-arrive`)
      this.strongbadWrapperTarget.classList.add(`animate-${this.strongbadAnimationValue}-leave`)
    }
  }

  hideAllStrongbads() {
    for (const strongbad of this.strongbadTargets) {
      strongbad.classList.add('hidden')
    }
  }

  showRandomStander() {
    this.hideAllStrongbads()

    const diceRoll = Math.floor(Math.random() * this.strongbadStandingTargets.length)
    this.strongbadStandingTargets[diceRoll].classList.remove('hidden')
  }

  showRandomFace() {
    this.hideAllStrongbads()

    const diceRoll = Math.floor(Math.random() * this.strongbadFaceTargets.length)
    this.strongbadFaceTargets[diceRoll].classList.remove('hidden')
  }

  animateMouth() {
    if (this.strongbadTalkingValue) {
      for (const mouth of this.openMouthTargets) {
        mouth.classList.toggle('hidden')
      }
      setTimeout(this.animateMouth.bind(this), 83.33)
    } else {
      for (const mouth of this.openMouthTargets) {
        mouth.classList.add('hidden')
      }
    }
  }

  showCards() {
    document.getElementById('crime_card').classList.add('hidden')
    document.getElementById('cards_wrapper').classList.remove('hidden')
  }

  hideCards() {
    document.getElementById('crime_card').classList.remove('hidden')
    document.getElementById('cards_wrapper').classList.add('hidden')
  }

  playCardAudio(e) {
    const howl = new Howl({ src: [ e.params.audio ] })
    howl.play()
  }

  toggleMenu() {
    const menuWrapper = document.getElementById('crime_menu_wrapper')
    menuWrapper.classList.remove('!animate-menu-stayopen')
    menuWrapper.classList.toggle('pointer-events-none')
    menuWrapper.classList.toggle('animate-menu-open')
    menuWrapper.classList.toggle('animate-menu-close')
  }

  showMenu(e) {
    this.menuVisibleValue = true

    document.getElementById(`menu_${e.params.menu}_wrapper`).classList.remove('hidden')
  }

  closeMenu() {
    if (this.menuVisibleValue) {
      document.querySelector('.menu:not(.hidden)').classList.add('hidden')
      const menuWrapper = document.getElementById('crime_menu_wrapper')
      if (menuWrapper.classList.contains('animate-menu-open')) {
        menuWrapper.classList.add('!animate-menu-stayopen')
      }

      this.menuVisibleValue = false
    }
  }

  menuVisibleValueChanged() {
    if (this.menuVisibleValue) {
      document.getElementById('crime_menu_wrapper').classList.add('hidden')
      document.getElementById('crime_menu_button').classList.add('hidden')
    } else {
      document.getElementById('crime_menu_wrapper').classList.remove('hidden')
      document.getElementById('crime_menu_button').classList.remove('hidden')
    }
  }

  toggleTimer() {
    document.getElementById('crime_stopwatch').classList.toggle('hidden')
    document.getElementById('crime_timer').classList.toggle('hidden')
  }

  forceTimer() {
    document.getElementById('crime_stopwatch').classList.add('hidden')
    document.getElementById('crime_timer').classList.remove('hidden')
  }

  addTime() {
    this.timeValue = Math.min(this.timeValue + 15, 120)  // Add up to cap of 2:00
  }

  subtractTime() {
    this.timeValue = Math.max(this.timeValue - 15, 0) // Subtract down to cap of 0:00
  }

  timeValueChanged() {
    const [minutes, seconds] = [Math.floor(this.timeValue / 60), this.timeValue % 60]

    this.minutesTarget.innerHTML = minutes
    this.secondsTarget.innerHTML = String(seconds).padStart(2, '0')
  }

  timerTick() {
    if (this.timerRunningValue) {
      this.timeValue = Math.max(this.timeValue - 1, 0) // Subtract down to cap of 0:00

      if (this.timeValue == 0) {
        this.guiltCheck()
        this.toggleTimerStart()
      } else {
        this.timerTimeout = setTimeout(this.timerTick.bind(this), 1000)
        if (this.timeValue == 9) {
          this.forceTimer()
          this.sfx.timerCrescendo.fade(0.0, 1.0, 3000)
          this.sfx.timerCrescendo.play()
          if (this.musicPlayingValue) {
            this.bgm[this.bgmPlayingValue].fade(this.musicVolumeValue, 0.0, 3000)
          }
        } else if (this.timeValue == 5) {
          if (this.musicPlayingValue) {
            this.pauseBGM()
            this.musicWasPlayingValue = true
          } else {
            this.musicWasPlayingValue = false
          }
        }
      }
    }
  }

  toggleTimerStart() {
    if (this.timerRunningValue) {
      this.timerButtonTarget.classList.remove('started')
      clearTimeout(this.timerTimeout)
      this.timerRunningValue = false
      this.timerTimeout = null
      if (this.timeValue < 10 && this.timeValue > 0) {
        this.sfx.timerCrescendo.pause()
      }
    } else {
      if (this.timeValue == 0) {
        this.timeValue = 60
      } else if (this.timeValue < 10) {
        this.sfx.timerCrescendo.play()
      }
      this.timerButtonTarget.classList.add('started')
      this.timerRunningValue = true
      this.timerTimeout = setTimeout(this.timerTick.bind(this), 1000)
    }
  }

  guiltCheck() {
    this.closeAllMenus()
    this.guiltCheckValue = true
    this.guiltCheckTarget.classList.remove('opacity-1')
    this.guiltCheckTarget.classList.remove('animate-guiltcheck-leave')
    this.guiltCheckTarget.classList.add('animate-guiltcheck-arrive')
    this.sfx.buzzer.play()
    setTimeout(this.guiltCheckStrongbad.bind(this), 500)
  }

  guiltCheckStrongbad() {
    this.strongbadValue = true
    this.sfx.guiltcheck.play()
    this.strongbadTalkingValue = true
    this.animateMouth()
    setTimeout(this.stopTalking.bind(this), 1000)
    setTimeout(this.endGuiltCheck.bind(this), 3000)
  }

  endGuiltCheck() {
    this.strongbadValue = false
    this.guiltCheckValue = false
    if (this.musicWasPlayingValue) {
      this.resumeBGM()
      this.bgm[this.bgmPlayingValue].volume(this.musicVolumeValue)
    }
    this.guiltCheckTarget.classList.add('animate-guiltcheck-leave')
    this.guiltCheckTarget.classList.remove('animate-guiltcheck-arrive')
  }

  stopTalking() {
    this.strongbadTalkingValue = false
  }

  closeAllMenus() {
    this.closeMenu()
    this.hideCards()
  }

  showCharacter(e) {
    document.getElementById(`character_${e.params.character}`).classList.remove('hidden')
    document.getElementById('menu_characters_menu').scrollTo({ top: 0 })
  }

  closeCharacter() {
    document.querySelector('.character:not(.hidden)').classList.add('hidden')
    document.getElementById('menu_characters_menu').scrollTo({ top: 0 })
  }

  toggleMusicPlayer() {
    document.getElementById('crime_music_button').classList.toggle('animate-musicbutton-shove')
    document.getElementById('crime_music_button').classList.toggle('animate-musicbutton-unshove')
    document.getElementById('music_menu_wrapper').classList.toggle('animate-musicplayer-arrive')
    document.getElementById('music_menu_wrapper').classList.toggle('animate-musicplayer-leave')
  }

  toggleMusicShuffle() {
    this.musicShuffleValue = !this.musicShuffleValue
    if (this.musicShuffleValue) {
      document.getElementById('music_shuffle').classList.add('!opacity-100')
    } else {
      document.getElementById('music_shuffle').classList.remove('!opacity-100')
    }
  }

  toggleMusicLoop() {
    this.musicLoopValue = !this.musicLoopValue
    if (this.musicLoopValue) {
      document.getElementById('music_loop').classList.add('!opacity-100')
    } else {
      document.getElementById('music_loop').classList.remove('!opacity-100')
    }
  }

  endOfSong() {
    if (this.musicLoopValue == false) {
      this.nextSong()
    } else {
      // this.bgm[this.bgmPlayingValue].seek(0) (actually nm just use howler's loop, it's easier)
    }
  }
}
